// The fact-find totals that the platform COMPUTES (SEP-243 section 9,
// acceptance point 23, and SEP-257 items 6, 7 and 8).
//
// The individual paper form prints four total boxes under its rows, and the
// platform copied each box as a typed field. A typed total drifts: the IM types
// 1,250,000, then edits one row, and the total keeps the old figure. Nothing
// tells anyone that the two disagree.
//
// This module is the one pure helper that derives each total from its rows. A
// total is NEVER stored: the form renders it, the filled PDF prints it, and the
// Internal Review reads it, all from the same function. This follows the
// precedent that `suitability-assessment-compute.ts` sets - store the raw
// inputs, and compute every derived value once.
//
// # The four totals
//
// SEP-243 made `total_assets` computed. SEP-257 items 6, 7 and 8 make the other
// three computed as well:
//
//   - `total_income` sums the income rows.
//   - `total_expenditure` sums the three expenditure rows.
//   - `total_liabilities` sums the mortgage and the other material debts. The
//     debts row was free text, which is why the total stayed typed until now.
//     SEP-257 adds `other_material_debts_amount`, a currency row, and the old
//     free-text row becomes a read-only legacy answer.
//
// A STORED value under any of the four ids is never read again. The computed
// value wins on every surface, and the write seam refuses the id
// (`fact-find-write-rules.ts`).
//
// # The "Existing Clients" form
//
// A suitability refresh asks the questions of the shorter "Existing Clients"
// form (SEP-288). That form prints the same four totals over nearly the same
// rows. It splits the Dowgate portfolio into a GIA row and an ISA row, and it
// asks for the income withdrawn from the portfolio and the portfolio yield in
// place of the two Dowgate income rows. Each form answers only its own rows,
// so one list for each total serves both forms: a row that a form does not
// ask is unanswered, and it adds nothing.
//
// # The entity forms
//
// The entity form of `entity-fact-find.pdf` prints no total box. The entity
// form of SEP-351 ("Suitability Entity Fact Find - Existing Entity Clients")
// prints the four totals over its own rows. The ids of those rows are not on
// the earlier entity form, so one list for each total serves both entity
// forms: a version of the earlier form answers no row, and each total stays
// null for it.

import type { FactFindAnswer } from './contracts/fact-find';
import {
    ENTITY_SUITABILITY_TOTAL_ASSETS_COMPONENT_IDS,
    ENTITY_SUITABILITY_TOTAL_EXPENDITURE_COMPONENT_IDS,
    ENTITY_SUITABILITY_TOTAL_INCOME_COMPONENT_IDS,
    ENTITY_SUITABILITY_TOTAL_LIABILITIES_COMPONENT_IDS,
} from './fact-find-entity-suitability';

/** The two canonical Dowgate forms. Re-stated here to avoid an import cycle. */
export type FactFindTotalsClientType = 'individual' | 'entity';

/**
 * The asset rows of the individual forms, in the order the onboarding form
 * prints them. Every one is a currency question, so the sum is well defined.
 */
export const TOTAL_ASSETS_COMPONENT_IDS: readonly string[] = [
    'dowgate_portfolio_value',
    // The "Existing Clients" form only (SEP-288).
    'dowgate_gia_value',
    'dowgate_isa_value',
    'cash_savings_nsi',
    'main_property_value',
    'other_property_value',
    'personal_pension_value',
    'other_pension_value',
    'business_interests_value',
    'other_investments_value',
    'other_material_assets_value',
];

/** The two liability rows of the individual form (SEP-257 item 8). */
export const TOTAL_LIABILITIES_COMPONENT_IDS: readonly string[] = [
    'mortgage_outstanding',
    'other_material_debts_amount',
];

/** The income rows of the individual forms (SEP-257 item 7, SEP-288). */
export const TOTAL_INCOME_COMPONENT_IDS: readonly string[] = [
    'employment_income',
    'dowgate_taxable_income',
    'state_pension_income',
    'dowgate_isa_income',
    'other_pension_income',
    'other_investment_income',
    // The "Existing Clients" form only (SEP-288).
    'dowgate_income_withdrawn',
    'dowgate_portfolio_yield',
    'all_other_income',
];

/** The three expenditure rows of the individual form (SEP-257 item 6). */
export const TOTAL_EXPENDITURE_COMPONENT_IDS: readonly string[] = [
    'annual_mortgage_rent',
    'annual_essential_expenditure',
    'annual_discretionary_expenditure',
];

/** The question id that holds the computed total assets on the individual form. */
export const TOTAL_ASSETS_QUESTION_ID = 'total_assets';

/**
 * One computed total, and the rows it sums.
 *
 * The table is the ONE declaration. The form, the PDF filler and the write seam
 * all read it, so a new total is one row here and not three copies.
 */
export interface FactFindTotalDefinition {
    readonly totalId: string;
    readonly componentIds: readonly string[];
}

/** Every computed total of the INDIVIDUAL form, in the order the form prints them. */
export const FACT_FIND_INDIVIDUAL_TOTALS: readonly FactFindTotalDefinition[] = [
    { totalId: TOTAL_ASSETS_QUESTION_ID, componentIds: TOTAL_ASSETS_COMPONENT_IDS },
    { totalId: 'total_liabilities', componentIds: TOTAL_LIABILITIES_COMPONENT_IDS },
    { totalId: 'total_income', componentIds: TOTAL_INCOME_COMPONENT_IDS },
    { totalId: 'total_expenditure', componentIds: TOTAL_EXPENDITURE_COMPONENT_IDS },
];

/** Every computed total of the ENTITY forms, in the order the SEP-351 form prints them. */
export const FACT_FIND_ENTITY_TOTALS: readonly FactFindTotalDefinition[] = [
    {
        totalId: TOTAL_ASSETS_QUESTION_ID,
        componentIds: ENTITY_SUITABILITY_TOTAL_ASSETS_COMPONENT_IDS,
    },
    {
        totalId: 'total_liabilities',
        componentIds: ENTITY_SUITABILITY_TOTAL_LIABILITIES_COMPONENT_IDS,
    },
    { totalId: 'total_income', componentIds: ENTITY_SUITABILITY_TOTAL_INCOME_COMPONENT_IDS },
    {
        totalId: 'total_expenditure',
        componentIds: ENTITY_SUITABILITY_TOTAL_EXPENDITURE_COMPONENT_IDS,
    },
];

/** Every computed total of one client type. */
export function factFindTotals(
    clientType: FactFindTotalsClientType,
): readonly FactFindTotalDefinition[] {
    return clientType === 'individual' ? FACT_FIND_INDIVIDUAL_TOTALS : FACT_FIND_ENTITY_TOTALS;
}

/** The component rows of one total, and an empty list when the form holds none. */
export function factFindTotalComponentIds(
    totalId: string,
    clientType: FactFindTotalsClientType = 'individual',
): readonly string[] {
    return factFindTotals(clientType).find((t) => t.totalId === totalId)?.componentIds ?? [];
}

/** The component rows of the total assets of one client type. */
export function totalAssetsComponentIds(clientType: FactFindTotalsClientType): readonly string[] {
    return factFindTotalComponentIds(TOTAL_ASSETS_QUESTION_ID, clientType);
}

/**
 * Read one answer as a number. A stored currency answer is a number, but a
 * live form control holds the raw keyboard entry as a string, so both shapes
 * arrive here. Anything else counts as unanswered.
 */
function numericAnswer(value: FactFindAnswer | undefined): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '') return null;
        const parsed = Number(trimmed);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
}

/** The outcome of the calculation, with the evidence a surface needs to show it. */
export interface FactFindTotalAssets {
    /** The sum of the answered rows, and null when no row is answered. */
    readonly total: number | null;
    /** How many component rows hold a value. */
    readonly answeredComponents: number;
    /** How many component rows the form prints. */
    readonly totalComponents: number;
}

/**
 * One computed total, derived from its rows in `answers`.
 *
 * An unanswered row counts as zero, because a client who leaves "Other
 * property" blank holds no other property. When NO row is answered the total is
 * null, which the surface renders as a dash rather than as a false zero.
 *
 * A stored answer under `totalId` itself never enters the sum, because the id
 * is not one of its own components.
 */
export function computeFactFindTotal(
    totalId: string,
    answers: Record<string, FactFindAnswer>,
    clientType: FactFindTotalsClientType = 'individual',
): FactFindTotalAssets {
    const componentIds = factFindTotalComponentIds(totalId, clientType);
    let sum = 0;
    let answered = 0;
    for (const id of componentIds) {
        const value = numericAnswer(answers[id]);
        if (value === null) continue;
        sum += value;
        answered += 1;
    }
    return {
        total: answered === 0 ? null : sum,
        answeredComponents: answered,
        totalComponents: componentIds.length,
    };
}

/** Total assets, derived from the asset rows of `answers`. */
export function computeTotalAssets(
    answers: Record<string, FactFindAnswer>,
    clientType: FactFindTotalsClientType = 'individual',
): FactFindTotalAssets {
    return computeFactFindTotal(TOTAL_ASSETS_QUESTION_ID, answers, clientType);
}

/**
 * Whether a question id holds a computed value on `clientType`. A computed
 * question is read-only in the form and it is never written to a response row.
 */
export function isComputedFactFindQuestion(
    questionId: string,
    clientType: FactFindTotalsClientType = 'individual',
): boolean {
    return factFindTotals(clientType).some((t) => t.totalId === questionId);
}
